import { createElement } from "react";
import { createRoot, type Root } from "react-dom/client";
import {
  AiChat,
  type ChatUser,
  type AiChatProps,
} from "./AiChat";
import { buildBrowserClientContext } from "./context";
import type { ClientContext, ToolCall } from "@maintainer-pro/ai-cli";
import { startChatUnreadPoller } from "./chat-unread";
import {
  createAiUiLogger,
  isAiUiDebugEnabled,
  resolveAiUiLogLevel,
  setAiUiDebug,
} from "./log";

declare const __EMBED_CSS__: string;
declare const __AI_UI_VERSION__: string;

const PACKAGE_VERSION =
  typeof __AI_UI_VERSION__ === "string" ? __AI_UI_VERSION__ : "0.0.0";

const embedLog = createAiUiLogger("embed");

const PANEL_WIDTH = "clamp(17.5rem, 32vw, 24rem)";
const MIN_PANEL_PX = 280;
const MAX_PANEL_PX = 960;

const SHELL_CSS = `
:root{--ai-ui-panel-width:${PANEL_WIDTH}}
[data-ai-ui-shell]{position:fixed;inset:0 0 0 auto;width:var(--ai-ui-panel-width);border-left:1px solid #e7e5e4;background:#fff;transform:translateX(100%);transition:transform .3s ease;z-index:2147483000;display:flex;flex-direction:column;overflow:hidden;overscroll-behavior:contain;font-family:system-ui,sans-serif}
[data-ai-ui-shell][data-layout=overlay]{box-shadow:-8px 0 24px rgb(0 0 0 / 8%);width:min(100%,var(--ai-ui-panel-width))}
[data-ai-ui-shell][data-open=true]{transform:translateX(0)}
[data-ai-ui-shell][data-resizing=true]{transition:none}
[data-ai-ui-resize]{position:absolute;top:0;bottom:0;left:0;z-index:3;width:.55rem;border:0;padding:0;background:transparent;cursor:ew-resize;touch-action:none}
[data-ai-ui-resize]::before{content:"";position:absolute;top:50%;left:2px;width:3px;height:2.75rem;transform:translateY(-50%);border-radius:999px;background:#d6d3d1;pointer-events:none}
[data-ai-ui-resize]:hover::before,[data-ai-ui-shell][data-resizing=true] [data-ai-ui-resize]::before{background:#6366f1}
[data-ai-ui-shell] [data-ai-ui-close]{position:absolute;top:.75rem;right:.75rem;z-index:2;width:2rem;height:2rem;border:0;border-radius:999px;background:transparent;color:#a8a29e;font-size:1.25rem;line-height:1;cursor:pointer}
[data-ai-ui-shell] [data-ai-ui-close]:hover{background:#f5f5f4;color:#44403c}
[data-ai-ui-shell] [data-ai-ui-panel]{flex:1;min-height:0;height:100%;overflow:hidden;overscroll-behavior:contain}
[data-ai-ui-fab-wrap]{position:fixed;right:1.25rem;bottom:1.25rem;z-index:2147483001;display:flex;align-items:center;gap:.7rem;transition:opacity .2s ease,transform .2s ease}
[data-ai-ui-fab-wrap][data-hidden=true]{opacity:0;pointer-events:none;transform:scale(.92)}
[data-ai-ui-fab-label]{background:#111827;color:#fff;font:650 .8rem/1 system-ui,sans-serif;letter-spacing:.01em;padding:.62rem .95rem;border-radius:999px;box-shadow:0 8px 22px rgb(17 24 39 / 22%);white-space:nowrap;animation:ai-ui-fab-nudge 2.8s ease-in-out infinite}
[data-ai-ui-fab]{position:relative;width:4rem;height:4rem;border:0;border-radius:999px;background:linear-gradient(145deg,#2563eb 0%,#4f46e5 55%,#7c3aed 100%);color:#fff;box-shadow:0 10px 28px rgb(79 70 229 / 48%),0 0 0 5px rgb(79 70 229 / 16%);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:transform .2s ease,filter .2s ease}
[data-ai-ui-fab]:hover{transform:scale(1.07);filter:brightness(1.08)}
[data-ai-ui-fab]::after{content:"";position:absolute;inset:-7px;border-radius:999px;border:2px solid rgb(99 102 241 / .55);animation:ai-ui-fab-ring 2.2s ease-out infinite;pointer-events:none}
[data-ai-ui-fab-badge]{position:absolute;top:-.1rem;right:-.1rem;min-width:1.2rem;height:1.2rem;padding:0 .3rem;border-radius:999px;background:#ef4444;color:#fff;font:700 .68rem/1.2rem system-ui,sans-serif;text-align:center;box-shadow:0 0 0 2px #fff;pointer-events:none}
[data-ai-ui-fab-badge][data-hidden=true]{display:none}
@keyframes ai-ui-fab-ring{0%{transform:scale(.85);opacity:.7}100%{transform:scale(1.28);opacity:0}}
@keyframes ai-ui-fab-nudge{0%,70%,100%{transform:translateX(0)}78%{transform:translateX(-4px)}86%{transform:translateX(3px)}94%{transform:translateX(-2px)}}
html[data-ai-ui-push]{transition:padding-right .3s ease}
html[data-ai-ui-push][data-open=true]{box-sizing:border-box;padding-right:var(--ai-ui-panel-width);overflow-x:hidden}
[data-ai-ui-push]:not(html){transition:margin-right .3s ease}
[data-ai-ui-push]:not(html)[data-open=true]{margin-right:var(--ai-ui-panel-width)}
html[data-ai-ui-resizing]{cursor:ew-resize;user-select:none}
html[data-ai-ui-resizing][data-ai-ui-push],[data-ai-ui-push][data-resizing=true]{transition:none}
@media (prefers-reduced-motion:reduce){[data-ai-ui-fab]::after,[data-ai-ui-fab-label]{animation:none}[data-ai-ui-shell],html[data-ai-ui-push],[data-ai-ui-push]:not(html){transition:none}}
`;

export type AiUiToolHandlers = Record<
  string,
  (args: Record<string, unknown>) => void | Promise<void>
>;

export type AiUiMountOptions = {
  /** Chat API endpoint (default: "/api/chat"). */
  apiUrl?: string;
  title?: string;
  conversationId?: string;
  className?: string;
  height?: string;
  users?: ChatUser[];
  /**
   * Extra fields merged into the browser client context each turn.
   * Prefer this over writing a full `buildContext`.
   */
  getData?: () => Record<string, unknown>;
  data?: Record<string, unknown>;
  relevantFiles?: string[];
  visiblePanels?: string[];
  /** Full control over context. Overrides getData / data helpers. */
  buildContext?: () => ClientContext;
  /** Map of tool name → handler. Convenience over `onToolCalls`. */
  tools?: AiUiToolHandlers;
  onToolCalls?: (toolCalls: ToolCall[]) => void | Promise<void>;
  captureState?: () => unknown;
  restoreState?: (snapshot: unknown) => void;
  /**
   * Isolate widget styles in a shadow root (default: true).
   * Set false only if the host page already provides Tailwind for the chat UI.
   */
  shadow?: boolean;
  /** Maintainer Pro base URL for direct chat/tenant reads (client API key). */
  maintainerProUrl?: string;
  /** Client-scoped Maintainer Pro API key (never use a server key in the browser). */
  maintainerProApiKey?: string;
  /** ai-server WebSocket URL (default: derived from apiUrl → `/api/ws`). */
  aiServerWsUrl?: string;
};

export type AiUiInitOptions = AiUiMountOptions & {
  /**
   * Chat API endpoint. Optional when `/embed-config.js` has already set
   * `window.__MAINTAINER_PRO__.apiUrl`.
   */
  apiUrl?: string;
  /**
   * `side` (default) docks chat on the right and shrinks the host page so
   * the app stays fully visible. `overlay` slides over the page.
   */
  layout?: "side" | "overlay";
  /**
   * Element that shrinks when the side panel opens.
   * Defaults to the document (`html`) for `layout: "side"`.
   */
  pushSelector?: string;
  storageKey?: string;
  startOpen?: boolean;
};

type EmbedConfig = {
  aiServerUrl?: string;
  apiUrl?: string;
  aiServerWsUrl?: string;
  maintainerProUrl?: string;
  maintainerProApiKey?: string;
};

function readEmbedConfig(): EmbedConfig {
  if (typeof window === "undefined") return {};
  return (
    (window as Window & { __MAINTAINER_PRO__?: EmbedConfig }).__MAINTAINER_PRO__ ??
    {}
  );
}

/** Merge explicit options with `/embed-config.js`. Explicit options win. */
function resolveInitOptions(options: AiUiInitOptions): AiUiInitOptions & {
  apiUrl: string;
} {
  const cfg = readEmbedConfig();
  const apiUrl = options.apiUrl || cfg.apiUrl;
  if (!apiUrl) {
    throw new Error(
      "AiUi.init requires options.apiUrl or window.__MAINTAINER_PRO__.apiUrl from /embed-config.js"
    );
  }
  return {
    ...options,
    apiUrl,
    maintainerProUrl: options.maintainerProUrl || cfg.maintainerProUrl,
    maintainerProApiKey:
      options.maintainerProApiKey || cfg.maintainerProApiKey,
    aiServerWsUrl: options.aiServerWsUrl || cfg.aiServerWsUrl,
  };
}

type MountRecord = {
  root: Root;
  host: HTMLElement;
  styleEl: HTMLStyleElement | null;
};

type InitRecord = {
  rootEl: HTMLElement;
  pushEl: HTMLElement | null;
  unmountChat: () => void;
  setOpen: (open: boolean) => void;
  cleanupResize: () => void;
};

function isNextHost(): boolean {
  return Boolean(
    typeof window !== "undefined" &&
      (window as Window & { next?: unknown }).next
  );
}

function isReactOwnedRoot(el: Element | null): boolean {
  return el === document.documentElement || el === document.body;
}

function resolvePushElement(selector?: string): HTMLElement | null {
  if (selector) {
    const el = document.querySelector(selector);
    if (el instanceof HTMLElement && !isReactOwnedRoot(el)) return el;
    return null;
  }
  // Next owns <html>/<body>. Attributes there break hydration.
  if (isNextHost()) return null;
  return document.documentElement;
}

/** Wait until Next (or similar) has hydrated before touching the DOM. */
function runAfterHostReady(fn: () => void): () => void {
  let done = false;
  let loadTimer: ReturnType<typeof setTimeout> | null = null;
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  const run = () => {
    if (done) return;
    done = true;
    if (loadTimer) clearTimeout(loadTimer);
    if (fallbackTimer) clearTimeout(fallbackTimer);
    fn();
  };
  const afterLoad = () => {
    if (isNextHost()) {
      loadTimer = setTimeout(run, 800);
      return;
    }
    requestAnimationFrame(() => requestAnimationFrame(run));
  };
  if (document.readyState === "complete") {
    afterLoad();
  } else {
    window.addEventListener("load", afterLoad, { once: true });
  }
  fallbackTimer = setTimeout(run, 4000);
  return () => {
    done = true;
    if (loadTimer) clearTimeout(loadTimer);
    if (fallbackTimer) clearTimeout(fallbackTimer);
    window.removeEventListener("load", afterLoad);
  };
}

const mounts = new WeakMap<Element, MountRecord>();
let sharedStyleEl: HTMLStyleElement | null = null;
let shellStyleEl: HTMLStyleElement | null = null;
let propertyStyleEl: HTMLStyleElement | null = null;
let activeInit: InitRecord | null = null;

const TW_PROPERTY_RE = /@property\s+[^{]+\{[^}]*\}/g;

function resolveElement(target: string | Element): Element {
  if (typeof target !== "string") return target;
  const el = document.querySelector(target);
  if (!el) {
    throw new Error(`AiUi.mount: no element matches "${target}"`);
  }
  return el;
}

function ensureDocumentStyle(): HTMLStyleElement {
  if (sharedStyleEl?.isConnected) return sharedStyleEl;
  const style = document.createElement("style");
  style.setAttribute("data-ai-ui-embed", "");
  style.textContent = __EMBED_CSS__;
  document.head.appendChild(style);
  sharedStyleEl = style;
  return style;
}

/**
 * `@property` inside a shadow-root stylesheet is ignored by browsers, so
 * Tailwind v4 border/shadow utilities have no defaults. Register them on
 * the document instead (https://github.com/tailwindlabs/tailwindcss/issues/15005).
 */
function ensureTailwindProperties(): void {
  if (typeof document === "undefined") return;
  if (propertyStyleEl?.isConnected) return;
  const existing = document.head.querySelector(
    "style[data-ai-ui-tw-properties]"
  );
  if (existing) {
    propertyStyleEl = existing as HTMLStyleElement;
    return;
  }
  const rules = __EMBED_CSS__.match(TW_PROPERTY_RE);
  if (!rules?.length) return;
  const style = document.createElement("style");
  style.setAttribute("data-ai-ui-tw-properties", "");
  style.textContent = rules.join("\n");
  document.head.appendChild(style);
  propertyStyleEl = style;
}

function ensureShellStyle(): void {
  if (shellStyleEl?.isConnected) {
    shellStyleEl.textContent = SHELL_CSS;
    return;
  }
  const existing = document.head.querySelector("style[data-ai-ui-shell-css]");
  if (existing instanceof HTMLStyleElement) {
    existing.textContent = SHELL_CSS;
    shellStyleEl = existing;
    return;
  }
  const style = document.createElement("style");
  style.setAttribute("data-ai-ui-shell-css", "");
  style.textContent = SHELL_CSS;
  document.head.appendChild(style);
  shellStyleEl = style;
}

function resolveBuildContext(
  options: AiUiMountOptions
): () => ClientContext {
  return () => {
    const page = buildBrowserClientContext({
      visiblePanels: options.visiblePanels,
      relevantFiles: options.relevantFiles,
      data: options.getData?.() ?? options.data,
    });
    if (!options.buildContext) return page;
    try {
      const custom = options.buildContext();
      return {
        ...page,
        ...custom,
        pageUrl: custom.pageUrl || page.pageUrl,
        route: custom.route || page.route,
        pageTitle: custom.pageTitle || page.pageTitle,
        data:
          page.data || custom.data
            ? { ...page.data, ...custom.data }
            : undefined,
      };
    } catch {
      return page;
    }
  };
}

function resolveOnToolCalls(
  options: AiUiMountOptions
): AiChatProps["onToolCalls"] {
  if (!options.tools && !options.onToolCalls) return undefined;

  return async (toolCalls) => {
    if (options.tools) {
      for (const tc of toolCalls) {
        const handler = options.tools[tc.tool];
        if (handler) await handler(tc.args ?? {});
      }
    }
    await options.onToolCalls?.(toolCalls);
  };
}

function toChatProps(options: AiUiMountOptions): AiChatProps {
  return {
    apiUrl: options.apiUrl ?? "/api/chat",
    title: options.title ?? "AI Assistant",
    conversationId: options.conversationId,
    className: options.className,
    users: options.users,
    buildContext: resolveBuildContext(options),
    onToolCalls: resolveOnToolCalls(options),
    captureState: options.captureState,
    restoreState: options.restoreState,
    maintainerProUrl: options.maintainerProUrl,
    maintainerProApiKey: options.maintainerProApiKey,
    aiServerWsUrl: options.aiServerWsUrl,
  };
}

/**
 * Mount the Maintainer Pro chat widget into any DOM element.
 *
 * @example
 * ```html
 * <div id="chat" style="height: 100vh"></div>
 * <script src="ai-ui.iife.js"></script>
 * <script>
 *   AiUi.mount("#chat", { apiUrl: "/api/chat" });
 * </script>
 * ```
 */
/** Keep wheel movement inside the chat thread instead of scrolling the host page. */
function trapChatWheel(event: WheelEvent) {
  const path = event.composedPath();
  const scroller = path.find(
    (node): node is HTMLElement =>
      node instanceof HTMLElement && node.classList.contains("aui-thread-scroll")
  );
  if (!scroller) return;
  const max = scroller.scrollHeight - scroller.clientHeight;
  if (max <= 0) return;
  const atTop = scroller.scrollTop <= 0 && event.deltaY < 0;
  const atBottom = scroller.scrollTop >= max - 1 && event.deltaY > 0;
  if (atTop || atBottom) event.preventDefault();
}

export function mount(
  target: string | Element,
  options: AiUiMountOptions = {}
): { unmount: () => void } {
  const el = resolveElement(target);
  embedLog.debug(
    {
      apiUrl: options.apiUrl,
      conversationId: options.conversationId,
      mp: Boolean(options.maintainerProUrl),
    },
    "mount"
  );
  unmount(el);

  const host = document.createElement("div");
  host.setAttribute("data-ai-ui-root", "");
  host.style.width = "100%";
  host.style.height = options.height ?? "100%";
  host.style.minHeight = options.height ? "0" : "320px";
  host.style.overflow = "hidden";
  host.style.overscrollBehavior = "contain";
  host.style.boxSizing = "border-box";

  let mountNode: HTMLElement;
  let styleEl: HTMLStyleElement | null = null;
  const useShadow = options.shadow !== false;

  if (useShadow) {
    ensureTailwindProperties();
    const shadow = host.attachShadow({ mode: "open" });
    styleEl = document.createElement("style");
    styleEl.textContent = __EMBED_CSS__;
    shadow.appendChild(styleEl);
    mountNode = document.createElement("div");
    mountNode.style.height = "100%";
    mountNode.style.width = "100%";
    mountNode.style.minHeight = "0";
    mountNode.style.overflow = "hidden";
    shadow.appendChild(mountNode);
  } else {
    ensureDocumentStyle();
    mountNode = host;
  }

  el.appendChild(host);
  host.addEventListener("wheel", trapChatWheel, { passive: false });

  const root = createRoot(mountNode);
  root.render(createElement(AiChat, toChatProps(options)));
  mounts.set(el, { root, host, styleEl });

  return {
    unmount: () => unmount(el),
  };
}

/** Remove a previously mounted widget from the target element. */
export function unmount(target: string | Element): void {
  const el =
    typeof target === "string" ? document.querySelector(target) : target;
  if (!el) return;

  const record = mounts.get(el);
  if (!record) return;

  record.root.unmount();
  record.host.remove();
  mounts.delete(el);
}

/**
 * Drop-in side-panel chat (FAB + docked panel). Load `/embed-config.js`
 * first so `apiUrl` / Maintainer Pro keys come from the server; host
 * options override.
 *
 * @example
 * ```html
 * <script src="/embed-config.js"></script>
 * <script src="/ai-ui.iife.js"></script>
 * <script>
 *   AiUi.init({ title: "AI Assistant" });
 * </script>
 * ```
 */
export function init(options: AiUiInitOptions = {}): {
  open: () => void;
  close: () => void;
  destroy: () => void;
} {
  if (typeof document === "undefined") {
    throw new Error("AiUi.init requires a browser document");
  }
  const resolved = resolveInitOptions(options);
  const layout = resolved.layout === "overlay" ? "overlay" : "side";
  embedLog.info(
    {
      version: PACKAGE_VERSION,
      apiUrl: resolved.apiUrl,
      maintainerProUrl: resolved.maintainerProUrl,
      conversationId: resolved.conversationId,
      layout,
      debug: isAiUiDebugEnabled(),
      logLevel: resolveAiUiLogLevel(),
    },
    "init"
  );

  destroy();
  ensureShellStyle();

  const start = () => mountChatShell(resolved, layout);
  const pending: {
    open: () => void;
    close: () => void;
    destroy: () => void;
  } = {
    open: () => {},
    close: () => {},
    destroy,
  };
  let live: ReturnType<typeof mountChatShell> | null = null;
  const cancel = runAfterHostReady(() => {
    live = start();
    pending.open = live.open;
    pending.close = live.close;
    pending.destroy = live.destroy;
  });
  pending.destroy = () => {
    cancel();
    if (live) live.destroy();
    else destroy();
  };
  return pending;
}

function panelWidthStorageKey(openStorageKey: string): string {
  return openStorageKey.endsWith(".chatOpen")
    ? openStorageKey.replace(/\.chatOpen$/, ".panelWidth")
    : `${openStorageKey}.panelWidth`;
}

function maxPanelPx(): number {
  const viewportCap = Math.max(160, window.innerWidth - 16);
  return Math.min(MAX_PANEL_PX, viewportCap);
}

function clampPanelWidth(px: number): number {
  const max = maxPanelPx();
  const min = Math.min(MIN_PANEL_PX, max);
  return Math.round(Math.min(max, Math.max(min, px)));
}

function readPanelWidth(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return clampPanelWidth(n);
  } catch {
    return null;
  }
}

function writePanelWidth(key: string, px: number | null): void {
  try {
    if (px == null) localStorage.removeItem(key);
    else localStorage.setItem(key, String(px));
  } catch {
    // ignore
  }
}

function setPanelWidthCss(px: number | null): void {
  const root = document.documentElement;
  if (px == null) root.style.removeProperty("--ai-ui-panel-width");
  else root.style.setProperty("--ai-ui-panel-width", `${px}px`);
}

function mountChatShell(
  resolved: ReturnType<typeof resolveInitOptions>,
  layout: "side" | "overlay"
): { open: () => void; close: () => void; destroy: () => void } {
  ensureShellStyle();

  const storageKey =
    resolved.storageKey ?? "ai-ui.embed.chatOpen";
  const pushEl =
    layout === "side"
      ? resolvePushElement(resolved.pushSelector)
      : null;
  if (pushEl) {
    pushEl.setAttribute("data-ai-ui-push", "");
  }

  const rootEl = document.createElement("div");
  rootEl.setAttribute("data-ai-ui-root-shell", "");

  const drawer = document.createElement("aside");
  drawer.setAttribute("data-ai-ui-shell", "");
  drawer.setAttribute("data-layout", layout);
  drawer.setAttribute("data-open", "false");
  drawer.setAttribute("aria-hidden", "true");

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.setAttribute("data-ai-ui-close", "");
  closeBtn.setAttribute("aria-label", "Close chat");
  closeBtn.textContent = "×";

  const resizeHandle = document.createElement("div");
  resizeHandle.setAttribute("data-ai-ui-resize", "");
  resizeHandle.setAttribute("role", "separator");
  resizeHandle.setAttribute("aria-orientation", "vertical");
  resizeHandle.setAttribute("aria-label", "Resize chat");
  resizeHandle.title = "Drag to resize. Double-click to reset.";
  resizeHandle.tabIndex = 0;

  const panel = document.createElement("div");
  panel.setAttribute("data-ai-ui-panel", "");

  drawer.append(resizeHandle, closeBtn, panel);

  const fabWrap = document.createElement("div");
  fabWrap.setAttribute("data-ai-ui-fab-wrap", "");
  fabWrap.setAttribute("data-hidden", "false");

  const fabLabel = document.createElement("span");
  fabLabel.setAttribute("data-ai-ui-fab-label", "");
  fabLabel.setAttribute("aria-hidden", "true");
  fabLabel.textContent = "Ask AI";

  const fab = document.createElement("button");
  fab.type = "button";
  fab.setAttribute("data-ai-ui-fab", "");
  fab.setAttribute("aria-label", "Open chat");
  fab.setAttribute("aria-expanded", "false");
  fab.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="30" height="30" fill="none" aria-hidden="true"><path fill="currentColor" d="M5.2 3.2A3.2 3.2 0 0 1 8.4 0h7.2A3.2 3.2 0 0 1 18.8 3.2v8.1a3.2 3.2 0 0 1-3.2 3.2h-5.1L6.2 18.4c-.72.54-1.76.02-1.76-.86V3.2z"/><circle fill="#fff" cx="8.6" cy="7.2" r="1.15"/><circle fill="#fff" cx="12" cy="7.2" r="1.15"/><circle fill="#fff" cx="15.4" cy="7.2" r="1.15"/><path fill="#fde68a" d="M19.1 1.1 19.7 2.7l1.7.06-1.35 1.04.5 1.62-1.45-.92-1.45.92.5-1.62-1.35-1.04 1.7-.06z"/></svg>`;

  const unreadBadge = document.createElement("span");
  unreadBadge.setAttribute("data-ai-ui-fab-badge", "");
  unreadBadge.setAttribute("data-hidden", "true");
  unreadBadge.setAttribute("aria-hidden", "true");
  fab.appendChild(unreadBadge);
  fabWrap.append(fabLabel, fab);

  rootEl.append(drawer, fabWrap);
  document.body.appendChild(rootEl);

  let open = false;
  let chatHandle: { unmount: () => void } | null = null;

  const setUnreadBadge = (count: number) => {
    if (count > 0) {
      unreadBadge.textContent = count > 99 ? "99+" : String(count);
      unreadBadge.setAttribute("data-hidden", "false");
      fab.setAttribute(
        "aria-label",
        count === 1 ? "Open chat, 1 unread message" : `Open chat, ${count} unread messages`
      );
    } else {
      unreadBadge.textContent = "";
      unreadBadge.setAttribute("data-hidden", "true");
      if (!open) fab.setAttribute("aria-label", "Open chat");
    }
  };

  const unreadPoller = startChatUnreadPoller({
    apiUrl: resolved.apiUrl,
    maintainerProUrl: resolved.maintainerProUrl,
    maintainerProApiKey: resolved.maintainerProApiKey,
    conversationId: resolved.conversationId,
    isOpen: () => open,
    onChange: setUnreadBadge,
  });

  const unmountChat = () => {
    chatHandle?.unmount();
    chatHandle = null;
    unmount(panel);
  };

  const setOpen = (next: boolean) => {
    open = next;
    drawer.setAttribute("data-open", next ? "true" : "false");
    drawer.setAttribute("aria-hidden", next ? "false" : "true");
    fabWrap.setAttribute("data-hidden", next ? "true" : "false");
    fab.setAttribute("aria-expanded", next ? "true" : "false");
    if (!next) {
      fab.setAttribute("aria-label", "Open chat");
    } else {
      fab.setAttribute("aria-label", "Close chat");
    }
    if (pushEl) {
      pushEl.setAttribute("data-open", next ? "true" : "false");
    }
    try {
      localStorage.setItem(storageKey, String(next));
    } catch {
      // ignore
    }
    if (next) {
      void unreadPoller.markRead();
      if (!chatHandle) {
        chatHandle = mount(panel, resolved);
      }
    } else {
      unmountChat();
      void unreadPoller.refresh();
    }
  };

  closeBtn.addEventListener("click", () => setOpen(false));
  fab.addEventListener("click", () => setOpen(!open));

  const widthKey = panelWidthStorageKey(storageKey);
  const storedWidth = readPanelWidth(widthKey);
  if (storedWidth != null) setPanelWidthCss(storedWidth);

  const resizeAbort = new AbortController();
  let dragging = false;
  let dragMoved = false;

  const syncResizeAria = () => {
    const max = maxPanelPx();
    const min = Math.min(MIN_PANEL_PX, max);
    const px = clampPanelWidth(drawer.getBoundingClientRect().width || storedWidth || min);
    resizeHandle.setAttribute("aria-valuemin", String(min));
    resizeHandle.setAttribute("aria-valuemax", String(max));
    resizeHandle.setAttribute("aria-valuenow", String(px));
  };

  const applyPanelWidth = (px: number, persist: boolean) => {
    const next = clampPanelWidth(px);
    setPanelWidthCss(next);
    if (persist) writePanelWidth(widthKey, next);
    syncResizeAria();
  };

  const stopResize = () => {
    if (!dragging) return;
    const shouldPersist = dragMoved;
    dragging = false;
    dragMoved = false;
    drawer.removeAttribute("data-resizing");
    pushEl?.removeAttribute("data-resizing");
    document.documentElement.removeAttribute("data-ai-ui-resizing");
    if (shouldPersist) {
      applyPanelWidth(drawer.getBoundingClientRect().width, true);
    }
  };

  resizeHandle.addEventListener(
    "pointerdown",
    (event) => {
      if (event.button !== 0) return;
      dragging = true;
      dragMoved = false;
      resizeHandle.setPointerCapture(event.pointerId);
      drawer.setAttribute("data-resizing", "true");
      pushEl?.setAttribute("data-resizing", "true");
      document.documentElement.setAttribute("data-ai-ui-resizing", "true");
      event.preventDefault();
    },
    { signal: resizeAbort.signal }
  );
  resizeHandle.addEventListener(
    "pointermove",
    (event) => {
      if (!dragging) return;
      dragMoved = true;
      applyPanelWidth(window.innerWidth - event.clientX, false);
    },
    { signal: resizeAbort.signal }
  );
  resizeHandle.addEventListener("pointerup", stopResize, {
    signal: resizeAbort.signal,
  });
  resizeHandle.addEventListener("pointercancel", stopResize, {
    signal: resizeAbort.signal,
  });
  resizeHandle.addEventListener(
    "dblclick",
    () => {
      writePanelWidth(widthKey, null);
      setPanelWidthCss(null);
      requestAnimationFrame(syncResizeAria);
    },
    { signal: resizeAbort.signal }
  );
  resizeHandle.addEventListener(
    "keydown",
    (event) => {
      const current = drawer.getBoundingClientRect().width;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        applyPanelWidth(current + 24, true);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        applyPanelWidth(current - 24, true);
      }
    },
    { signal: resizeAbort.signal }
  );
  syncResizeAria();

  const cleanupResize = () => {
    if (dragging) stopResize();
    resizeAbort.abort();
    drawer.removeAttribute("data-resizing");
    pushEl?.removeAttribute("data-resizing");
    document.documentElement.removeAttribute("data-ai-ui-resizing");
    setPanelWidthCss(null);
  };

  activeInit = {
    rootEl,
    pushEl,
    unmountChat: () => {
      unreadPoller.stop();
      unmountChat();
    },
    setOpen,
    cleanupResize,
  };

  let startOpen = resolved.startOpen ?? false;
  if (resolved.startOpen === undefined) {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === "true") startOpen = true;
      if (stored === "false") startOpen = false;
    } catch {
      // ignore
    }
  }
  if (startOpen) setOpen(true);
  return {
    open: () => setOpen(true),
    close: () => setOpen(false),
    destroy,
  };
}

/** Tear down a previous `AiUi.init()` instance. */
export function destroy(): void {
  if (!activeInit) return;
  embedLog.debug({}, "destroy");
  activeInit.cleanupResize();
  activeInit.unmountChat();
  if (activeInit.pushEl) {
    activeInit.pushEl.removeAttribute("data-ai-ui-push");
    activeInit.pushEl.removeAttribute("data-open");
    activeInit.pushEl.removeAttribute("data-resizing");
  }
  activeInit.rootEl.remove();
  activeInit = null;
}

export { buildBrowserClientContext, setAiUiDebug as setDebug };
export { PACKAGE_VERSION };
export type { ClientContext, ToolCall, ChatUser };
export type { LogLevel } from "./log";
